import {
    HistoryOperation,
    NextUrlParams,
    PopStateListener,
} from "./interfaces";

export default class AppHistory {
    private static listeners: PopStateListener[] = [];
    private static isManual = false;
    private static queue: HistoryOperation[] = [];
    private static manualThen: (() => void) | null = null;

    public static mount() {
        window.addEventListener("popstate", this.onPopState);
    }

    public static destroy() {
        window.removeEventListener("popstate", this.onPopState);
    }

    public static on(listener: PopStateListener) {
        this.listeners.push(listener);

        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    public static back(times = 1, then?: () => void) {
        if (this.queue.length === 0) {
            this.isManual = true;
            this.manualThen = then || null;
            window.history.go(-times);
        } else {
            this.queue.push({ type: "back", times, then });
        }

        return this;
    }

    public static push(params: NextUrlParams, then?: () => void) {
        if (this.queue.length === 0 && !this.isManual) {
            const url = this.getNextUrl(params);

            window.history.pushState(window.history.state, "", url);
        } else {
            this.queue.push({ type: "push", params, then });
        }

        return this;
    }

    public static replace(params: NextUrlParams, then?: () => void) {
        if (this.queue.length === 0 && !this.isManual) {
            const url = this.getNextUrl(params);

            window.history.replaceState(window.history.state, "", url);
        } else {
            this.queue.push({ type: "replace", params, then });
        }

        return this;
    }

    private static onPopState = (event: PopStateEvent) => {
        if (this.isManual && this.manualThen) {
            this.manualThen();
            this.manualThen = null;
        }

        while (this.queue.length > 0) {
            const item = this.queue.shift();

            if (!item) {
                break;
            }

            if (item.type === "push") {
                const url = this.getNextUrl(item.params);

                window.history.pushState(window.history.state, "", url);
                item.then?.();
            } else if (item.type === "replace") {
                const url = this.getNextUrl(item.params);

                window.history.replaceState(window.history.state, "", url);
                item.then?.();
            } else if (item.type === "back") {
                window.history.go(-item.times);
                this.manualThen = item.then || null;
                break;
            }
        }

        if (this.isManual) {
            this.isManual = false;
            return;
        }

        for (const listener of this.listeners) {
            listener(event);
        }
    };

    private static getNextUrl(params: NextUrlParams) {
        const url = new URL(window.location.href);

        Object.entries(params).forEach(([key, value]) => {
            if (value) {
                url.searchParams.set(key, value);
            } else if (value === null) {
                url.searchParams.delete(key);
            }
        });

        return url.toString();
    }
}
